<template>
    <div>
        <rkm-row v-if="data.model.avaliacaoAntropometrica.massa">
            <rkm-col class="mb-2">
                <rkm-alert show>Peso do paciente: {{ data.model.avaliacaoAntropometrica.massa }} {{ getUnidadeMedida(data.model.avaliacaoAntropometrica.massaUnidadeMedidaId).sigla }}</rkm-alert>
            </rkm-col>
        </rkm-row>

        <rkm-list
            :accordion="false"
            :fields="fields"
            :items="data.form.prescricoesInternas.concat(data.model.prescricoesInternas)"
            hide-filters
            hide-pagination
        >
            <template v-slot="{ item }">
                <rkm-row>
                    <rkm-col
                        cols="auto"
                        class="my-auto px-0 info"
                    >
                        <div v-if="Number.isInteger(item.id)">
                            <rkm-icon
                                :id="`pronto-atendimento-prescricao-interna-info-${item.id}`"
                                font-scale="1.5"
                                icon="info-circle"
                            />

                            <rkm-popover
                                :target="`pronto-atendimento-prescricao-interna-info-${item.id}`"
                                triggers="hover"
                            >
                                <div>{{ item.centroCusto.setor.nome }}</div>
                                <div><rkm-icon icon="user" /> {{ item.lotacao.profissional.nome }} - {{ item.lotacao.ocupacao.nome }}</div>
                                <div>Class. risco: {{ item.classificacaoRisco.nome }}</div>
                                <div><rkm-icon :icon="['far', 'clock']" /> {{ item.dataHora }}</div>
                            </rkm-popover>
                        </div>
                    </rkm-col>

                    <rkm-col>
                        <rkm-row>
                            <rkm-col cols="4">
                                <div>{{ item.medicamentos.length.toString().padStart(2, '0') }}</div>
                                <div>Medicamento(s)</div>
                            </rkm-col>

                            <rkm-col cols="auto">
                                <div v-if="item.doseUnica"><strong>Dose única</strong></div>
                                <div v-else><strong>Frequência:</strong> {{ item.frequenciaQuantidadeVezes }} {{ parseInt(item.frequenciaQuantidadeVezes) > 1 ? 'aplicações' : 'aplicação' }}, uma a cada {{ item.frequenciaTempo }} {{getUnidadesMedidaTempo(item.frequenciaTempo, item.frequenciaMedidaTempoId)}}</div>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col class="pt-2">
                                <div v-if="item.observacoes">
                                    <rkm-icon :icon="['far', 'comment-dots']" /> {{ item.observacoes }}
                                </div>
                            </rkm-col>
                        </rkm-row>
                    </rkm-col>
                </rkm-row>
            </template>

            <template v-slot:trailling="{ item }">
                <rkm-button
                    @click.stop="handleReplicarPrescricaoInterna(item)"
                    color="info"
                    small
                    title="Replicar prescrição interna"
                >
                    <rkm-icon icon="reply" />
                </rkm-button>

                <rkm-button
                    :disabled="Number.isInteger(item.id)"
                    @click.stop="$emit('edit', item)"
                    color="primary"
                    title="Editar"
                    small
                >
                    <rkm-icon icon="pen" />
                </rkm-button>

                <rkm-button
                    :disabled="Number.isInteger(item.id)"
                    @click.stop="handleDestroy(item)"
                    color="danger"
                    title="Excluir"
                    small
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>

            <template v-slot:collapse="{ item }">
                <rkm-list-group class="rkm-list-group">
                    <rkm-list-group-item
                        v-for="medicamento in item.medicamentos"
                        v-bind:key="medicamento.id"
                        class="back"
                    >
                        <rkm-row class="align-items-center">
                            <rkm-col class="flex-grow-1">
                                <div>
                                    <strong>
                                        {{ medicamento.medicamento.nome }}
                                    </strong>
                                    - {{medicamento.quantidade.toString().replace('.', ',')}} {{medicamento.unidadeMedidaSigla}} {{medicamento.formaFarmaceuticaNome}}
                                </div>

                                <div v-if="medicamento.administracao">
                                    <rkm-icon :icon="['far', 'comment-dots']" /> {{ medicamento.administracao }}
                                </div>
                            </rkm-col>
                        </rkm-row>

                        <hr>

                        <rkm-row>
                            <rkm-col>
                                <span>
                                    <strong> Status de Aplicação </strong>
                                </span>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row v-if="!medicamento.aplicacoes">
                            <rkm-col>
                                <span>Não há aplicação correspondente à sua solicitação</span>
                            </rkm-col>
                        </rkm-row>

                        <rkm-row
                            v-else
                            class="d-flex mt-2"
                        >
                            <rkm-col
                                v-for="(aplicacao, index) in medicamento.aplicacoes"
                                :key="aplicacao.id"
                                cols="2"
                                class="text-center aplicacao"
                                :class="{
                                    'btn-success': aplicacao.status.id === enums.status.REALIZADO,
                                    'btn-info': aplicacao.status.id === enums.status.SOLICITADO,
                                    'btn-danger': aplicacao.status.id === enums.status.NAO_REALIZADO
                                }"
                            >
                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            <strong>{{`${index + 1}ª Aplicação`}}</strong>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <span>
                                            {{ aplicacao.status.nome }}
                                            <template v-if="aplicacao.status.id === enums.status.REALIZADO && aplicacao.horarioAplicacao !== null">
                                                às {{ aplicacao.horarioAplicacao | formatTime }}
                                            </template>
                                        </span>
                                    </rkm-col>
                                </rkm-row>

                                <template v-if="aplicacao.status.id !== enums.status.SOLICITADO">
                                    <rkm-row class="mt-1">
                                        <rkm-col>
                                            <span>
                                                <strong>Profissional</strong>
                                            </span>
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <span>
                                                {{ aplicacao.profissional.nome }}
                                            </span>
                                        </rkm-col>
                                    </rkm-row>
                                </template>
                            </rkm-col>
                        </rkm-row>
                    </rkm-list-group-item>
                </rkm-list-group>
            </template>
        </rkm-list>
    </div>
</template>

<script>
// Filters.
import formats from '@/filters/formats'

// Enums.
import medidaTempo from '@/enums/cadastro/medidaTempo'
import unidadeMedida from '@/enums/cadastro/unidadeMedida'
import status from '@/enums/pronto-atendimento/statusExame'

export default {
    data: () => ({
        fields: [
            { key: 'info', label: '#', class: 'col-min text-center' },
            { key: 'codigo', label: 'Código', class: 'col-min' },
            { key: 'procedimento.nome', label: 'Nome' },
            { key: 'actions', label: 'Ações', class: 'col-min' },
        ],
        enums: {
            medidaTempo,
            unidadeMedida,
            status,
        },
    }),
    filters: {
        ...formats,
    },
    methods: {
        getUnidadesMedidaTempo(tempo, id) {
            const unidadeMedidaTempo = this.data.form.unidadesMedidaTempo.find(medida => medida.id === id)

            let nome = unidadeMedidaTempo ? unidadeMedidaTempo.nome : ''

            if (nome && tempo < 2) {

                nome = nome.replace('(s)', '')
            }

            nome = nome.toLowerCase()

            return nome
        },
        getUnidadeMedida(id) {
            return this.data.form.unidadesMedida.find(unidade => unidade.id === id)
        },
        handleDestroy(item) {
            const destroy = () => new Promise(resolve => {
                const index = this.data.model.prescricoesInternas.findIndex(procedimento => procedimento.id === item.id)
                resolve(this.data.model.prescricoesInternas.splice(index, 1))
            })

            this.showDeleteConfirmation('Tem certeza que deseja excluir?', destroy)
        },
        handleReplicarPrescricaoInterna(prescricaoInterna) {
            this.data.model.prescricoesInternas.push({
                lotacao: {
                    ocupacao: {
                        codigo: this.data.form.lotacao.ocupacao.codigo,
                    },
                    profissional: {
                        nome: this.data.form.lotacao.profissional.nome,
                    },
                },
                id: this.uniqueId('pronto-atendimento-atendimento-medico-prescricao-interna'),
                frequenciaQuantidadeVezes: prescricaoInterna.frequenciaQuantidadeVezes,
                frequenciaTempo: prescricaoInterna.frequenciaTempo,
                frequenciaMedidaTempoId: prescricaoInterna.frequenciaMedidaTempoId,
                observacoes: prescricaoInterna.observacoes,
                medicamentos: prescricaoInterna.medicamentos.map(medicamento => {
                    return {
                        administracao: medicamento.administracao,
                        formaFarmaceuticaNome: medicamento.formaFarmaceuticaNome,
                        medicamento: medicamento.medicamento,
                        quantidade: parseInt(medicamento.quantidade),
                        unidadeMedidaId: medicamento.unidadeMedidaId,
                        unidadeMedidaSigla: medicamento.unidadeMedidaSigla,
                    }
                }),
                doseUnica: prescricaoInterna.doseUnica,
                collapse: true,
            })
        },
    },
    props: {
        data: { type: Object, required: true },
    },
}
</script>

<style scoped>
.info {
    width: 25px;
}
.aplicacao.btn-success {
    padding: 16px;
    margin: 6px;
}
.aplicacao.btn-danger{
    padding: 16px;
    margin: 6px;
}
.aplicacao.btn-info {
    padding: 16px;
    margin: 6px;
}
.back{
    background-color:#f4f4f4;
}
</style>